import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import {
  RelativeFilterChoice,
  RelativeFilterConfigBody,
  RelativeFilterResponseBody,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {newPresetDefaultConfig} from './newPresetDefaultConfig';

const intOrNull = z.union([
  z.null(),
  z.number().superRefine((val, ctx) => {
    if (!Number.isInteger(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.sourcing.relativeFilters.dialog.integerRequired'),
      });
    }
  }),
]);

/** 100% cap: feature score preset offsets, and max vehicle − for mileage/power/price %. */
const MAX_PERCENT = 100;

const numericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable();

/**
 * Mileage / power / price %: `from` is 0 or in [−100, 0]; `to` is 0 or any positive % (vehicle +).
 */
const vehicleMetricPctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const signMessage = i18n.t(
      'entity.sourcing.relativeFilters.dialog.pctVehicleOffsetSignInvalid'
    );
    const belowMaxMessage = i18n.t(
      'entity.sourcing.relativeFilters.dialog.pctVehicleBelowPercentMax'
    );
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: signMessage,
          path: ['from'],
        });
      } else if (bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: belowMaxMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0 && bound.to < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: signMessage,
        path: ['to'],
      });
    }
  });

/**
 * Feature score % offsets: magnitudes must stay within 0–100 from the vehicle level.
 */
const featureScorePctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const pctRangeMessage = i18n.t('entity.sourcing.relativeFilters.dialog.pctOffsetOutOfRange');
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0 || bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0) {
      if (bound.to < 0 || bound.to > MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['to'],
        });
      }
    }
  });

const relativeFilterChoiceSchema = z.union([z.literal('same'), z.literal('same_better'), z.null()]);

const twoWayRelativeFilterChoiceSchema = z.union([z.literal('same'), z.null()]);

export const presetEditorFormSchema = z.object({
  name: z.string().superRefine((val, ctx) => {
    if (!isNotEmpty(trim(val))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.sourcing.relativeFilters.dialog.presetNameRequired'),
      });
    }
  }),
  year: numericBoundSchema,
  mileage: numericBoundSchema,
  mileagePct: vehicleMetricPctNumericBoundSchema,
  power: numericBoundSchema,
  powerPct: vehicleMetricPctNumericBoundSchema,
  price: numericBoundSchema,
  pricePct: vehicleMetricPctNumericBoundSchema,
  featureScore: featureScorePctNumericBoundSchema,
  trimLevel: relativeFilterChoiceSchema,
  transmission: relativeFilterChoiceSchema,
  fuel: twoWayRelativeFilterChoiceSchema,
  body: twoWayRelativeFilterChoiceSchema,
  drive: relativeFilterChoiceSchema,
  vatDeductible: relativeFilterChoiceSchema,
  sellerType: z.array(z.string()).nullable(),
  /**
   * Passthrough only. Relative filter presets are shared storage with price-report, and this
   * editor rebuilds `config` from the zod-parsed form values — an undeclared key would be
   * stripped by `z.object`, silently wiping a selection made in price-report. Sourcing
   * deliberately neither renders nor resolves it: its presets resolve on the BE (T20-138339).
   */
  server: z.array(z.string()).nullable().optional(),
  seats: relativeFilterChoiceSchema,
  radius: z.union([
    z.null(),
    z.number().superRefine((val, ctx) => {
      if (!Number.isInteger(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.sourcing.relativeFilters.dialog.integerRequired'),
        });
        return;
      }
      if (val < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.sourcing.relativeFilters.validation.radiusPositive'),
        });
      }
    }),
  ]),
  features: z.union([z.null(), z.array(z.string())]),
  mileageSnapToServerSteps: z.boolean().nullable(),
  mileageMin: z.union([
    z.null(),
    z.number().superRefine((val, ctx) =>
      match(val)
        .when(
          (value) => !Number.isInteger(value),
          () =>
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t('entity.sourcing.relativeFilters.dialog.integerRequired'),
            })
        )
        .when(
          (value) => value < 0,
          () =>
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t('entity.sourcing.relativeFilters.validation.mileageMinPositive'),
            })
        )
        .otherwise(() => undefined)
    ),
  ]),
  isDefault: z.boolean(),
});

export type PresetEditorFormValues = z.infer<typeof presetEditorFormSchema>;

function twoWayRelativeChoiceFromApi(value: RelativeFilterChoice | null): 'same' | null {
  return match<RelativeFilterChoice | null, 'same' | null>(value)
    .with(null, () => null)
    .with('same', () => 'same')
    .with('same_better', () => 'same')
    .exhaustive();
}

/** Coerces legacy RelativeFilterChoice strings (`'same'` / `'same_better'`) to null. */
function sellerTypeFromApi(value: RelativeFilterConfigBody['sellerType']): string[] | null {
  return Array.isArray(value) ? value : null;
}

function normalizeConfigForPresetEditor(
  config: RelativeFilterConfigBody
): Omit<PresetEditorFormValues, 'name' | 'isDefault'> {
  return {
    ...config,
    fuel: twoWayRelativeChoiceFromApi(config.fuel),
    body: twoWayRelativeChoiceFromApi(config.body),
    sellerType: sellerTypeFromApi(config.sellerType),
    features: config.features ?? null,
    mileageSnapToServerSteps: config.mileageSnapToServerSteps ?? null,
    mileageMin: config.mileageMin ?? null,
  };
}

export function buildPresetEditorDefaultValues(
  initialPreset: RelativeFilterResponseBody | null
): PresetEditorFormValues {
  return {
    name: initialPreset?.name ?? '',
    isDefault: initialPreset?.default ?? false,
    ...normalizeConfigForPresetEditor(initialPreset?.config ?? newPresetDefaultConfig()),
  };
}

export function getRelativeFilterConfigFromPresetEditorForm(
  values: PresetEditorFormValues
): RelativeFilterConfigBody {
  const {name: _name, isDefault: _isDefault, ...config} = values;

  // The mileage extras only exist alongside an active mileage bound; the minimum
  // additionally makes no sense with From = “same as vehicle” (it could never apply).
  const activeMileageBound = config.mileage ?? config.mileagePct;
  const isSnapStorable = isNotNil(activeMileageBound);
  const isMinStorable = isNotNil(activeMileageBound) && activeMileageBound.from !== 0;

  return {
    ...config,
    mileageSnapToServerSteps: isSnapStorable ? (config.mileageSnapToServerSteps ?? null) : null,
    mileageMin: isMinStorable ? config.mileageMin : null,
  };
}
